import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Chip,
  styled,
} from '@mui/material';
import RecommendationModal from '@/screens/optimization/components/recommendation-modal';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { T } from '@/theme/tokens';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: '500px',
    maxWidth: '800px',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: T.surface,
  color: T.text,
  borderBottom: `1px solid ${T.divider}`,
  '& .MuiTypography-root': {
    fontSize: '1.25rem',
    fontWeight: 900,
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: T.surfaceMuted,
}));

const RecommendationListItem = styled(ListItemButton)(({ theme }) => ({
  border: `1px solid ${T.border}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(2),
  '&:hover': {
    backgroundColor: '#F5F9FE',
  },
}));

const SavingsText = styled(Typography)(({ theme }) => ({
  color: T.success,
  fontWeight: 'bold',
}));

const ImpactChip = styled(Chip)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

interface Recommendation {
  id: number;
  name: string;
  type: string;
  impact: string;
  monthly_savings: number;
  yearly_savings?: number;
  currency: string;
  status: string;
  description?: string;
  action?: string;
  date_created: string;
  date_deadline?: string;
}

interface RecommendationsListModalProps {
  open: boolean;
  onClose: () => void;
  recommendations: Recommendation[];
  resourceName: string;
}

const RecommendationsListModal: React.FC<RecommendationsListModalProps> = ({
  open,
  onClose,
  recommendations,
  resourceName,
}) => {
  const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);

  const handleRecommendationClick = (recommendation: Recommendation) => {
    setSelectedRecommendation(recommendation);
  };

  const handleCloseDetailModal = () => {
    setSelectedRecommendation(null);
  };

  const getImpactColor = (impact: string) => {
    switch (impact?.toLowerCase()) {
      case 'high':
        return T.warn;
      case 'medium':
        return T.warnDark;
      case 'low':
        return T.success;
      default:
        return T.success;
    }
  };

  if (!open || !recommendations || recommendations.length === 0) {
    return null;
  }

  // If only one recommendation, show the detail modal directly
  if (recommendations.length === 1) {
    return (
      <RecommendationModal
        recommendation={recommendations[0]}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      <StyledDialog open={open} onClose={onClose} maxWidth="md">
        <StyledDialogTitle>
          <Typography variant="h6">
            Recommendations for {resourceName}
          </Typography>
        </StyledDialogTitle>

        <StyledDialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Found {recommendations.length} active recommendations for this resource. 
            Click on any recommendation to view details.
          </Typography>

          <List>
            {recommendations.map((recommendation) => (
              <RecommendationListItem
                key={recommendation.id}
                onClick={() => handleRecommendationClick(recommendation)}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle1" fontWeight="medium">
                        {recommendation.name}
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <SavingsText variant="body2">
                          {formatCurrencyNoUnit(recommendation.monthly_savings)} {recommendation.currency}/mo
                        </SavingsText>
                        <ImpactChip
                          label={recommendation.impact}
                          size="small"
                          sx={{
                            backgroundColor: getImpactColor(recommendation.impact),
                            color: 'white',
                          }}
                        />
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {recommendation.type} • {recommendation.status}
                      </Typography>
                      {recommendation.description && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {recommendation.description.length > 100 
                            ? `${recommendation.description.substring(0, 100)}...`
                            : recommendation.description
                          }
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </RecommendationListItem>
            ))}
          </List>
        </StyledDialogContent>

        <StyledDialogActions>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </StyledDialogActions>
      </StyledDialog>

      {/* Detail modal for individual recommendations */}
      {selectedRecommendation && (
        <RecommendationModal
          recommendation={selectedRecommendation}
          onClose={handleCloseDetailModal}
        />
      )}
    </>
  );
};

export default RecommendationsListModal;
